import type { CheckIssue, CheckResult } from '@tabservice/tabfile-client';
import { Pill } from '@/components/ui/pill';
import { Table, TableWrap, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { formatCount } from '@/lib/format';
import { explanationFor } from './issue-codes';

interface Grouped {
  key: string;
  code: string;
  severity: CheckIssue['severity'];
  message: string;
  field?: string;
  rows: number[];
  refs: string[];
}

/**
 * Issues repeat across rows, so group them and list the row numbers. A group is one kind of
 * fault: the code, the field and the rule. Grouping by code alone put a blank box 3 and a
 * duplicate recipient TIN, both MISSING_FIELD, under the first one's message (TS-8704).
 */
export function issueGroupKey(issue: Pick<CheckIssue, 'code' | 'field'> & { rule?: string }): string {
  return `${issue.code}:${issue.field ?? ''}:${issue.rule ?? ''}`;
}

export function groupIssues(issues: CheckIssue[]): Grouped[] {
  const map = new Map<string, Grouped>();
  for (const issue of issues) {
    const key = `${issueGroupKey(issue)}:${issue.severity}`;
    const existing = map.get(key);
    // Rows are one based for people, the API index is zero based.
    // A null index means the issue is about the file, not one record.
    const row = typeof issue.index === 'number' ? issue.index + 1 : null;
    if (existing) {
      if (row !== null) existing.rows.push(row);
      if (issue.recordRef) existing.refs.push(issue.recordRef);
    } else {
      map.set(key, {
        key,
        code: issue.code,
        severity: issue.severity,
        message: issue.message,
        field: issue.field,
        rows: row === null ? [] : [row],
        refs: issue.recordRef ? [issue.recordRef] : [],
      });
    }
  }
  return [...map.values()].sort((a, b) => {
    if (a.severity === b.severity) return b.rows.length - a.rows.length;
    return a.severity === 'error' ? -1 : 1;
  });
}

function listSummary(values: (string | number)[], limit: number): string {
  const shown = values.slice(0, limit).join(', ');
  return values.length > limit ? `${shown} and ${formatCount(values.length - limit)} more` : shown;
}

/**
 * Where the issue is. Row numbers when the file had rows, the record
 * references when it did not, and "the whole file" when it is neither.
 */
function whereSummary(group: Grouped): string {
  if (group.rows.length > 0) return listSummary(group.rows, 8);
  if (group.refs.length > 0) return listSummary(group.refs, 4);
  return 'the whole file';
}

/**
 * What a file-level issue affects.
 *
 * An issue with no record index is about the file: a duplicate upload, two form types in one
 * sheet. Every record in the file is held back by it, so the column said "-" on the issues
 * that block the most. It says so plainly now, with the count when the file was read far
 * enough to have one.
 */
export function wholeFileAffected(recordCount?: number): string {
  if (typeof recordCount === 'number' && recordCount > 0) {
    return `All ${formatCount(recordCount)}`;
  }
  return 'Whole file';
}

export function CheckSummary({ result }: { result: CheckResult }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Pill tone={result.blocked > 0 ? 'danger' : 'success'}>
        {result.blocked > 0 ? `${formatCount(result.blocked)} blocked` : 'Nothing blocked'}
      </Pill>
      <Pill tone="neutral">{formatCount(result.fileable)} ready to file</Pill>
      <Pill tone={result.issues.some((issue) => issue.severity === 'warning') ? 'warning' : 'neutral'}>
        {formatCount(result.issues.filter((issue) => issue.severity === 'warning').length)} warnings
      </Pill>
    </div>
  );
}

export function CheckIssues({ issues, recordCount }: { issues: CheckIssue[]; recordCount?: number }) {
  const groups = groupIssues(issues);

  if (groups.length === 0) {
    return <p className="text-base text-muted-foreground">Every row passed validation.</p>;
  }

  return (
    <TableWrap>
      <Table>
        <THead>
          <tr>
            <TH>Issue</TH>
            <TH>Field</TH>
            <TH align="right">Affected</TH>
            <TH>Where</TH>
          </tr>
        </THead>
        <TBody>
          {groups.map((group) => {
            const explanation = explanationFor(group.code);
            const affected = group.rows.length || group.refs.length;
            return (
              <TR key={group.key} className="h-auto">
                <TD className="py-3">
                  <p className="text-base text-gray-3">{group.message}</p>
                  {explanation && explanation !== group.message ? (
                    <p className="mt-1 max-w-lg text-base text-muted-foreground">{explanation}</p>
                  ) : null}
                  <p className="mt-0.5 text-xs text-gray-1">{group.code}</p>
                </TD>
                <TD className="text-muted-foreground">{group.field ? group.field : 'whole row'}</TD>
                <TD numeric>{affected === 0 ? wholeFileAffected(recordCount) : formatCount(affected)}</TD>
                <TD className="text-muted-foreground">{whereSummary(group)}</TD>
              </TR>
            );
          })}
        </TBody>
      </Table>
    </TableWrap>
  );
}

export function ColumnMapping({ mapping }: { mapping: Record<string, string> }) {
  const entries = Object.entries(mapping);
  if (entries.length === 0) {
    return <p className="text-base text-muted-foreground">No columns were matched automatically.</p>;
  }
  return (
    <TableWrap>
      <Table>
        <THead>
          <tr>
            <TH>Your column</TH>
            <TH>Read as</TH>
          </tr>
        </THead>
        <TBody>
          {entries.map(([source, canonical]) => (
            <TR key={source}>
              <TD>{source}</TD>
              <TD className="text-muted-foreground">{canonical}</TD>
            </TR>
          ))}
        </TBody>
      </Table>
    </TableWrap>
  );
}
